/**
 * What the handoff email can say without the rep typing it (ADR 0280).
 *
 * Reads; decides nothing. Which options were sold, which upgrades were passed
 * over, what the contract is, when the crew starts and how the email reads are
 * `handoffOptions`, `handoffUpgrades`, `handoffContract`, `handoffStart` and
 * `handoffEmail` in packages/core.
 */

import {
  checkStillExpected,
  handoffContract,
  handoffOptions,
  handoffStart,
  handoffUpgrades,
  jobAddress,
  jobColourOptions,
  toCents,
  type ColorProduct,
  type EstimateItem,
  type HandoffFacts,
  type HandoffPayMethod,
  type Job,
  type PresentedDocument,
  type SignedContract,
} from "@roof-hawk/core";

import { supabase } from "./supabase";
import { fmtDateOnly } from "./format";
import { fetchJobBalance, fetchLadderInputs, soldOptions } from "./queries";

interface SignedRow {
  id: string;
  total: number | string | null;
  estimate_id: string | null;
  extra_estimate_ids: string[] | null;
  upgrade_groups: string[] | null;
  customer_note: string | null;
  signed_date: string | null;
  accepted_at: string;
  // PostgREST can hand the embed back as one row or as a list of one.
  proposals:
    | { snapshot: PresentedDocument | null }
    | { snapshot: PresentedDocument | null }[]
    | null;
}

function signedSnapshot(row: SignedRow): PresentedDocument | null {
  const proposal = Array.isArray(row.proposals)
    ? row.proposals[0]
    : row.proposals;
  return proposal?.snapshot ?? null;
}

const PAY_METHODS: readonly HandoffPayMethod[] = [
  "card",
  "ach",
  "check",
  "cash",
  "financing",
];

/**
 * A crew's start, as a person says it: "Fri, 10/16".
 *
 * PostgREST can hand back `2026-10-16 13:00:00+00`, which `new Date` refuses;
 * the space and the short offset are mended first.
 */
function startDay(value: string): string {
  const iso = value.replace(" ", "T").replace(/([+-]\d\d)$/, "$1:00");
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleDateString([], {
    weekday: "short",
    month: "numeric",
    day: "numeric",
  });
}

/** Whatever `read` finds, or `fallback` when it fails. */
async function orElse<T>(read: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await read();
  } catch {
    return fallback;
  }
}

/**
 * The signatures that still stand. Voiding keeps the acceptance row as the
 * record of what was agreed, but takes its force away, so production must not
 * order from it.
 */
async function readSigned(jobId: string): Promise<SignedRow[]> {
  const { data, error } = await supabase
    .from("proposal_acceptances")
    .select(
      "id, total, estimate_id, extra_estimate_ids, upgrade_groups, " +
        "customer_note, signed_date, accepted_at, " +
        "proposals!inner(job_id, status, snapshot)",
    )
    .eq("proposals.job_id", jobId)
    .neq("proposals.status", "void")
    .order("accepted_at");
  if (error) throw error;
  return (data ?? []) as unknown as SignedRow[];
}

/** How the customer told the deposit link they would pay, where they have. */
async function readPayMethod(job: Job): Promise<HandoffPayMethod | null> {
  const { data, error } = await supabase
    .from("mercury_payment_requests")
    .select("customer_method")
    .eq("job_id", job.id)
    .eq("slot", 1)
    .neq("state", "cancelled")
    .not("customer_method", "is", null)
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) throw error;
  const method = (data?.[0] as { customer_method?: string } | undefined)
    ?.customer_method;
  return PAY_METHODS.find((value) => value === method) ?? null;
}

async function readDeposit(job: Job): Promise<HandoffFacts["deposit"]> {
  const first = (await fetchJobBalance(job)).lines.find(
    (line) => line.slot === 1,
  );
  return first && first.amount > 0
    ? { amount: first.amount, paid: first.paid }
    : null;
}

type Inputs =
  Awaited<ReturnType<typeof fetchLadderInputs>> extends Map<string, infer T>
    ? T
    : never;

/** The live signatures as contracts, for `handoffContract`. */
function contractsFrom(signed: readonly SignedRow[]): SignedContract[] {
  return signed.map((row) => ({
    id: row.id,
    total: toCents(row.total),
    estimateIds: signedEstimateIds(row),
    acceptedAt: row.accepted_at,
  }));
}

/** The soonest crew booked on what was sold, as a person says it. */
function firstStart(inputs: Inputs): string | null {
  const start = handoffStart(
    inputs
      .flatMap((input) => input.workOrders)
      .map((order) => (order as { start_at?: string | null }).start_at),
  );
  return start ? startDay(start) || null : null;
}

/** The colour rules and choices of the products on the job's lines. */
async function readProducts(
  ids: readonly (string | null | undefined)[],
): Promise<ColorProduct[]> {
  const wanted = [...new Set(ids.filter((id): id is string => Boolean(id)))];
  if (wanted.length === 0) return [];
  const { data, error } = await supabase
    .from("products")
    .select("id, name, color_rule, color_options")
    .in("id", wanted);
  if (error) throw error;
  return (data ?? []) as ColorProduct[];
}

function signedEstimateIds(row: SignedRow): string[] {
  return row.estimate_id
    ? [row.estimate_id, ...(row.extra_estimate_ids ?? [])]
    : [];
}

/**
 * Everything the job already records that the handoff asks about.
 *
 * Each part is read on its own and a part that fails is left out, never the
 * handoff: the email is still the rep's to finish, and a job with no estimate
 * or no signature is still a job somebody sold.
 */
export async function fetchHandoffFacts(
  job: Job,
  senderName: string,
): Promise<HandoffFacts> {
  const contact = job.contact ?? {};
  const [inputs, signed, deposit, payMethod] = await Promise.all([
    orElse(
      async () => (await fetchLadderInputs([job.id])).get(job.id) ?? [],
      [] as Inputs,
    ),
    orElse(() => readSigned(job.id), [] as SignedRow[]),
    orElse(() => readDeposit(job), null),
    orElse(() => readPayMethod(job), null),
  ]);
  const contract = await orElse(
    async () =>
      handoffContract({
        signed: contractsFrom(signed),
        options: soldOptions(job.job_type, inputs),
      }),
    null,
  );

  const estimates = inputs.map((input) => ({
    ...(input.estimate as unknown as {
      id: string;
      name?: string | null;
      version?: number | null;
    }),
    // Whole rows: `fetchLadderInputs` selects every column, and the ladder's
    // type only names the money ones.
    items: input.items as unknown as EstimateItem[],
  }));
  const products = await orElse(
    () =>
      readProducts(
        estimates.flatMap((estimate) =>
          estimate.items.map((item) => item.product_id),
        ),
      ),
    [] as ColorProduct[],
  );
  const upgrades = handoffUpgrades(
    signed.flatMap((row) => {
      const document = signedSnapshot(row);
      return document && row.estimate_id
        ? [
            {
              document,
              estimateIds: signedEstimateIds(row),
              upgradeKeys: row.upgrade_groups ?? [],
            },
          ]
        : [];
    }),
  );
  const latest = signed[signed.length - 1];

  return {
    repName: job.rep?.full_name ?? senderName,
    senderName,
    customerName: contact.full_name ?? "",
    phone: contact.cell ?? contact.phone ?? "",
    address: jobAddress(job),
    jobNumber: job.job_number,
    bidType: job.bid_type,
    options: handoffOptions(
      estimates,
      signed.flatMap(signedEstimateIds),
      products,
    ),
    colourOptions: jobColourOptions(products),
    upgradesChosen: upgrades.chosen,
    upgradesNotChosen: upgrades.notChosen,
    contract,
    signedOn: latest
      ? fmtDateOnly(latest.signed_date ?? latest.accepted_at) || null
      : null,
    scheduledStart: firstStart(inputs),
    deposit,
    payMethod,
    checkExpected: checkStillExpected(job.check_expected_at, new Date()),
    financing: Boolean(job.financing),
    customerNotes: signed
      .map((row) => row.customer_note ?? "")
      .filter((note) => note.trim() !== ""),
  };
}
